import { useCallback, useState } from 'react'
import type { BatchResponse } from '@/lib/api/types'
import { batchSummary } from '@/lib/batch'
import { quickEditErrorMessage } from '@/lib/quick-edit'

/** What a batch did: the summary, and each record that did not change, with why. */
export interface BatchReport {
  summary: string
  refused: Array<{ id: string; label: string; message: string }>
  /** Why the whole batch failed, when it never reached Helm or Helm refused it all. */
  detail?: string
}

interface ShownEdit<T> {
  /** The row as loaded. Once the list reloads, the edit no longer applies. */
  base: T
  row: T
}

/**
 * A list's rows with edits shown before the server answers. An edit shows at
 * once; when the save fails the row goes back to what it was and `failure`
 * says why, in words. Reloading the list replaces the rows, and with them any
 * edit still shown.
 */
export function useOptimisticRows<T extends { id: string }>(rows: readonly T[]) {
  const [shown, setShown] = useState<ReadonlyMap<string, ShownEdit<T>>>(() => new Map())
  const [saving, setSaving] = useState<ReadonlySet<string>>(() => new Set())
  const [failure, setFailure] = useState<string | null>(null)
  const [report, setReport] = useState<BatchReport | null>(null)
  // Stable, so a report can time itself out without restarting on every render.
  const dismissReport = useCallback(() => setReport(null), [])

  const visible = rows.map((row) => {
    const edit = shown.get(row.id)
    return edit && edit.base === row ? edit.row : row
  })

  /**
   * Shows `change` on the row, then saves it; true once Helm has it. `label`
   * names the record in the message if the save fails.
   */
  async function save(
    row: T,
    change: Partial<T>,
    request: () => Promise<Partial<T>>,
    label: string,
  ): Promise<boolean> {
    const base = rows.find((candidate) => candidate.id === row.id)
    if (!base) return false
    const previous = shown.get(row.id)
    setFailure(null)
    setShown((current) => new Map(current).set(row.id, { base, row: { ...row, ...change } }))
    setSaving((current) => new Set(current).add(row.id))
    try {
      const saved = await request()
      setShown((current) => {
        const edit = current.get(row.id)
        if (!edit || edit.base !== base) return current
        return new Map(current).set(row.id, { base, row: { ...edit.row, ...saved } })
      })
      return true
    } catch (error) {
      setShown((current) => {
        const next = new Map(current)
        if (previous?.base === base) next.set(row.id, previous)
        else next.delete(row.id)
        return next
      })
      setFailure(`${label} was not changed. ${quickEditErrorMessage(error)}`)
      return false
    } finally {
      setSaving((current) => {
        const next = new Set(current)
        next.delete(row.id)
        return next
      })
    }
  }

  /**
   * Shows every change at once, then saves them in one batch: what Helm
   * saved stays, what it refused goes back, and `report` says which and why.
   * Gives the ids that did not change, to keep them selected for a retry.
   * Rows that already had the change are not in `entries`.
   */
  async function saveBatch(
    entries: Array<{ row: T; change: Partial<T> }>,
    request: () => Promise<BatchResponse<Partial<T>>>,
    labelOf: (row: T) => string,
    pick: (saved: Partial<T>) => Partial<T>,
  ): Promise<string[]> {
    setFailure(null)
    if (!entries.length) {
      // Every selected row already had it; nothing to send.
      setReport({ summary: batchSummary(0, 0), refused: [] })
      return []
    }
    const ids = entries.map((entry) => entry.row.id)
    const bases = new Map(
      entries.map((entry) => [entry.row.id, rows.find((row) => row.id === entry.row.id)]),
    )
    const previous = new Map(entries.map((entry) => [entry.row.id, shown.get(entry.row.id)]))
    const putBack = (current: ReadonlyMap<string, ShownEdit<T>>, id: string) => {
      const next = new Map(current)
      const before = previous.get(id)
      if (before && before.base === bases.get(id)) next.set(id, before)
      else next.delete(id)
      return next
    }
    setReport(null)
    setShown((current) => {
      const next = new Map(current)
      for (const { row, change } of entries) {
        const base = bases.get(row.id)
        if (base) next.set(row.id, { base, row: { ...row, ...change } })
      }
      return next
    })
    setSaving((current) => new Set([...current, ...ids]))
    try {
      const answer = await request()
      const refused = answer.results.flatMap((result) =>
        result.ok
          ? []
          : [
              {
                id: result.id,
                label: labelOf(entries.find((entry) => entry.row.id === result.id)!.row),
                message: quickEditErrorMessage(result.error),
              },
            ],
      )
      setShown((current) => {
        let next: ReadonlyMap<string, ShownEdit<T>> = current
        for (const result of answer.results) {
          if (!result.ok) {
            next = putBack(next, result.id)
            continue
          }
          const edit = next.get(result.id)
          if (edit) {
            next = new Map(next).set(result.id, {
              base: edit.base,
              row: { ...edit.row, ...pick(result.record) },
            })
          }
        }
        return next
      })
      setReport({ summary: batchSummary(answer.updated, answer.failed), refused })
      return refused.map((entry) => entry.id)
    } catch (error) {
      setShown((current) => ids.reduce(putBack, current))
      setReport({
        summary: 'Nothing was changed',
        refused: [],
        detail: quickEditErrorMessage(error),
      })
      return ids
    } finally {
      setSaving((current) => {
        const next = new Set(current)
        for (const id of ids) next.delete(id)
        return next
      })
    }
  }

  return {
    rows: visible,
    saving,
    failure,
    dismissFailure: () => setFailure(null),
    save,
    report,
    dismissReport,
    saveBatch,
  }
}

export type OptimisticRows<T extends { id: string }> = ReturnType<typeof useOptimisticRows<T>>
